import React, { useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { CollapseBlock } from '../../../../common/index.jsx';
import { InputContainer } from '../../../../common/form/FormElements.jsx';
import { useTranslation } from 'react-i18next';
import FormTextField from '../../../../antd/form/FormTextField/FormTextField.jsx';
import Form from '../../../../antd/form/Form/Form.jsx';
import FormButton from '../../../../antd/form/FormButton/FormButton.jsx';
import { Alert } from 'antd';
import FormButtonsContainer from '../../../../antd/form/FormButtonsContainer/FormButtonsContainer.jsx';
import { useToastMessages } from '../../../../../hooks/useToastMessages.jsx';
import { capitalize } from 'lodash';
import { empty } from '../../../../antd/form/FormValidation.js';
import ResponsesTable from './ResponsesTable.jsx';
import { normalizeResponseName, RESPONSE_SOURCES } from './responseHelpers.js';
import ResponsesImport from './ResponsesImport.jsx';
import {
  useLazyGetSubjectResponsesQuery,
  useLazyGetTeachingStaffResponsesQuery,
} from '../../../../../redux/services/polls';

const ResponseForm = ({
  isCreate,
  showInfoMessage,
  initialValues,
  onSubmit,
}) => {
  const { t } = useTranslation();
  const newResponseCounter = useRef(0);
  const [responses, setResponses] = useState(() =>
    initialValues.responses.map(({ id, name, editable }) => ({
      key: `id-${id}`,
      id,
      name,
      editable,
    })),
  );

  const { apiErrorMessage, errorMessage, successMessage } = useToastMessages();
  const [getTeachingStaffResponses, { isFetching: isTeachingStaffLoading }] =
    useLazyGetTeachingStaffResponsesQuery();
  const [getSubjectResponses, { isFetching: isSubjectsLoading }] =
    useLazyGetSubjectResponsesQuery();

  const [importedSource, setImportedSource] = useState(null);
  // The other source is unlocked again once all responses are removed.
  const activeImportedSource = responses.length > 0 ? importedSource : null;

  const isUsedInPoll = initialValues.questions?.length > 0;

  const changeResponses = (newResponses) => {
    setResponses(newResponses);
    showInfoMessage();
  };

  /**
   * Append new responses, skipping names already present in the list.
   * @returns {number} number of added responses
   */
  const addResponses = (names) => {
    const existingNames = new Set(
      responses.map(({ name }) => normalizeResponseName(name)),
    );
    const newResponses = [];
    names.forEach((name) => {
      const normalized = normalizeResponseName(name);
      if (normalized && !existingNames.has(normalized)) {
        existingNames.add(normalized);
        newResponseCounter.current += 1;
        newResponses.push({
          key: `new-${newResponseCounter.current}`,
          id: null,
          name: name.trim(),
          editable: true,
        });
      }
    });
    if (newResponses.length > 0) {
      changeResponses([...responses, ...newResponses]);
    }
    return newResponses.length;
  };

  /**
   * Load names from a prefill source and append them to the responses.
   * @param {string} source one of RESPONSE_SOURCES
   * @param {Function} loadNames RTK Query lazy trigger returning names
   * @param {boolean} option source option passed to the trigger
   */
  const importResponses = async (source, loadNames, option) => {
    try {
      const names = await loadNames(option).unwrap();
      const addedCount = addResponses(names);
      setImportedSource(source);
      successMessage({
        message: t('Pridané odpovede') + `: ${addedCount}`,
      });
    } catch (err) {
      apiErrorMessage(err);
    }
  };

  const beforeSubmit = (values) => {
    if (responses.length < 2) {
      errorMessage({ message: t('Je potrebné zadať aspoň 2 odpovede.') });
      return;
    }
    const names = responses.map(({ name }) => normalizeResponseName(name));
    if (names.some((name) => !name)) {
      errorMessage({ message: t('Odpoveď nemôže byť prázdna.') });
      return;
    }
    if (new Set(names).size !== names.length) {
      errorMessage({ message: t('Každá odpoveď musí byť unikátna.') });
      return;
    }

    onSubmit({
      name: values.name,
      responses: responses.map(({ id, name }) => ({ id, name: name.trim() })),
    });
  };

  return (
    <div className="response-edit-wrapper">
      <Form
        onFinish={beforeSubmit}
        initialValues={{ name: initialValues.name }}
      >
        <CollapseBlock title={t('Základné informácie')} collapsible>
          <InputContainer>
            <FormTextField
              label={t('Názov kolekcie odpovedí')}
              name="name"
              rules={[empty(t)]}
            />
          </InputContainer>

          {isUsedInPoll && (
            <Alert
              type={'warning'}
              title={t(
                'Kolekcia odpovedí je už použitá v ankete (nie je možné zmazať existujúce odpovede): ',
              )}
              description={
                <ul>
                  {initialValues.questions.map((question) => (
                    <li key={question.id}>
                      <span style={{ fontWeight: 'bold' }}>Anketa: </span>
                      {`"${question.polls[0].name}"`}{' '}
                      <span style={{ fontWeight: 'bold' }}>
                        {t('Otázka')}:{' '}
                      </span>{' '}
                      {`"${capitalize(question.name)}" `}
                    </li>
                  ))}
                </ul>
              }
            />
          )}
        </CollapseBlock>

        <CollapseBlock title={t('Odpovede')} collapsible>
          {isCreate && (
            <ResponsesImport
              isTeachingStaffLoading={isTeachingStaffLoading}
              isSubjectsLoading={isSubjectsLoading}
              activeSource={activeImportedSource}
              onImportTeachingStaff={(includeExternal) =>
                importResponses(
                  RESPONSE_SOURCES.TEACHING_STAFF,
                  getTeachingStaffResponses,
                  includeExternal,
                )
              }
              onImportSubjects={(includePhd) =>
                importResponses(
                  RESPONSE_SOURCES.SUBJECTS,
                  getSubjectResponses,
                  includePhd,
                )
              }
            />
          )}
          <ResponsesTable
            responses={responses}
            isUsedInPoll={isUsedInPoll}
            onChange={changeResponses}
            onAdd={addResponses}
          />
        </CollapseBlock>

        <FormButtonsContainer>
          <FormButton submitOnClick={true} submitKey={'polls'}>
            {t('Uložiť')}
          </FormButton>
        </FormButtonsContainer>
      </Form>
    </div>
  );
};

ResponseForm.propTypes = {
  isCreate: PropTypes.bool,
  showInfoMessage: PropTypes.func,
  initialValues: PropTypes.any,
  onSubmit: PropTypes.func,
};

export default ResponseForm;
